import { useCallback, useContext, useMemo } from "react";
import dayjs from "dayjs";
import { useParams, useSearchParams } from "react-router-dom";
import useAuthentication from "../../../hooks/useAuthentication";
import { EmrContext } from "../../../EMR";
import type {
  VoicePromptRequest,
  VoicePromptType,
} from "../../../Types/VoiceAssistant/VoiceAssistantTypes";
import { useEmrUrlPatientIds } from "./useEmrUrlPatientIds";

function toPositiveId(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function useEmrPatientServiceContext() {
  const { encounterId: routeEncounterId, patientId: routePatientMasterId } =
    useParams();
  const emrIds = useEmrUrlPatientIds();
  const [searchParams] = useSearchParams();
  const { user } = useAuthentication();
  const { patientData } = useContext(EmrContext);

  const resolvedEncounterId = useMemo(
    () =>
      emrIds.encounterId ??
      toPositiveId(routeEncounterId) ??
      toPositiveId(patientData?.encounter?.encounterId) ??
      toPositiveId(patientData?.patientDetails?.encounterId),
    [
      emrIds.encounterId,
      patientData?.encounter?.encounterId,
      patientData?.patientDetails?.encounterId,
      routeEncounterId,
    ],
  );

  const resolvedPatientMasterId = useMemo(
    () =>
      emrIds.patientMasterId ??
      toPositiveId(routePatientMasterId) ??
      toPositiveId(patientData?.pmaster?.id) ??
      toPositiveId(patientData?.encounter?.pmasterId) ??
      toPositiveId(patientData?.patientDetails?.pmasterId) ??
      toPositiveId(patientData?.amaster?.patientMasterID),
    [
      emrIds.patientMasterId,
      patientData?.amaster?.patientMasterID,
      patientData?.encounter?.pmasterId,
      patientData?.patientDetails?.pmasterId,
      patientData?.pmaster?.id,
      routePatientMasterId,
    ],
  );

  const buildPatientServicePayload = useCallback(
    (type: VoicePromptType, prompt: string): VoicePromptRequest => {
      const emrNo =
        patientData?.pmaster?.patientId?.toString() ||
        emrIds.patientId ||
        searchParams.get("emrno")?.trim() ||
        null;

      return {
        date: dayjs().format("YYYY-MM-DD"),
        prompt,
        type,
        language: navigator.language || null,
        userId: user?.userId ?? user?.login_id ?? null,
        encounterId: resolvedEncounterId,
        patientMasterId: resolvedPatientMasterId,
        patientId: emrNo,
      };
    },
    [
      emrIds.patientId,
      patientData?.pmaster?.patientId,
      resolvedEncounterId,
      resolvedPatientMasterId,
      searchParams,
      user?.login_id,
      user?.userId,
    ],
  );

  const hasPatientContext = Boolean(
    resolvedEncounterId && resolvedPatientMasterId,
  );

  return {
    resolvedEncounterId,
    resolvedPatientMasterId,
    hasPatientContext,
    buildPatientServicePayload,
  };
}
